import { StreamChat } from 'stream-chat';

export type SendStreamMessageArgs = {
  /** The member's Vida user uuid — the Stream user id the token authenticates. */
  userUuid: string;
  /** The solo care-team's uuid — the `care-team` channel id (Sparta's teamUuid). */
  teamUuid: string;
  /** The message body to send. */
  text: string;
  /** The Stream auth token for `userUuid` (from GET /api/v1/stream-user-auth/). */
  authToken: string;
};

/**
 * Sends one message into the member's `care-team` Stream channel, headlessly —
 * connect → send → disconnect, no chat UI (mirrors Sparta's useStreamChat). The
 * `stream-chat` JS client is pure-JS/isomorphic, so this runs unchanged on web
 * and native.
 *
 * Returns `true` when the message was actually sent. No-ops (returns `false`)
 * when the Stream API key isn't provisioned, so unconfigured environments don't
 * error — and the caller doesn't mistake a no-op for a real send. Throws on a
 * real send failure; the caller (`sendIntroMessage`) treats sending as
 * best-effort and swallows it.
 */
export async function sendStreamMessage({
  userUuid,
  teamUuid,
  text,
  authToken,
}: SendStreamMessageArgs): Promise<boolean> {
  const apiKey = process.env.EXPO_PUBLIC_STREAM_CHAT_API_KEY;
  if (!apiKey) {
    if (__DEV__) console.warn('[sendStreamMessage] no-op — EXPO_PUBLIC_STREAM_CHAT_API_KEY unset');
    return false;
  }

  const client = StreamChat.getInstance(apiKey);
  try {
    await client.connectUser({ id: userUuid }, authToken);
    // The care-team channel already exists (created when the coach was assigned),
    // so we send directly without watch()/create() — same as Sparta.
    await client.channel('care-team', teamUuid).sendMessage({ text });
    return true;
  } finally {
    // Always release the websocket, even if connect/send threw.
    await client.disconnectUser();
  }
}
